import {
  BEACH_LEFT_Y,
  DAY_CYCLE_MS,
  WORLD_HEIGHT,
  WORLD_WIDTH,
  beachGroundY,
  standingGroundY,
} from "../game/constants";

interface Cloud {
  x: number;
  y: number;
  s: number;
  v: number;
}

interface Bird {
  x: number;
  y: number;
  v: number;
  phase: number;
}

export class WorldRenderer {
  private clouds: Cloud[] = [
    { x: 180, y: 72, s: 1, v: 12 },
    { x: 520, y: 46, s: 1.35, v: 8 },
    { x: 900, y: 86, s: 0.95, v: 15 },
    { x: 1100, y: 38, s: 1.15, v: 10 },
  ];
  private birds: Bird[] = [
    { x: 300, y: 118, v: 48, phase: 0 },
    { x: 800, y: 68, v: 36, phase: 1.2 },
    { x: 1080, y: 96, v: 42, phase: 2.1 },
  ];

  update(dt: number, reduceMotion: boolean): void {
    const speed = reduceMotion ? 0.2 : 1;
    for (const cloud of this.clouds) {
      cloud.x += cloud.v * dt * speed;
      if (cloud.x > WORLD_WIDTH + 140) cloud.x = -140;
    }
    for (const bird of this.birds) {
      bird.x += bird.v * dt * speed;
      bird.phase += dt * (reduceMotion ? 3 : 8);
      bird.y += Math.sin(bird.phase) * (reduceMotion ? 2 : 8) * dt;
      if (bird.x > WORLD_WIDTH + 40) {
        bird.x = -40;
        bird.y = 50 + Math.random() * 80;
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D, timeMs: number, reduceMotion: boolean, highContrast: boolean): void {
    const cycle = (Math.sin((timeMs / DAY_CYCLE_MS) * Math.PI * 2 - Math.PI / 2) + 1) / 2;
    const dayTop = [125, 210, 245];
    const setTop = [255, 132, 92];
    const dayBot = [186, 236, 255];
    const setBot = [255, 196, 120];
    const top = mix(dayTop, setTop, cycle);
    const bot = mix(dayBot, setBot, cycle);
    const sky = ctx.createLinearGradient(0, 0, 0, BEACH_LEFT_Y);
    sky.addColorStop(0, rgb(top));
    sky.addColorStop(1, rgb(bot));
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);

    const sunY = 90 + cycle * 70;
    const glow = ctx.createRadialGradient(1080, sunY, 8, 1080, sunY, 90);
    glow.addColorStop(0, `rgba(255, ${Math.round(230 - cycle * 40)}, 120, 0.9)`);
    glow.addColorStop(1, "rgba(255, 180, 80, 0)");
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(1080, sunY, 90, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = `rgba(255, ${Math.round(210 - cycle * 40)}, 70, 0.95)`;
    ctx.beginPath();
    ctx.arc(1080, sunY, 42, 0, Math.PI * 2);
    ctx.fill();

    for (const cloud of this.clouds) this.cloud(ctx, cloud, cycle);
    if (!reduceMotion) for (const bird of this.birds) this.bird(ctx, bird);

    this.ocean(ctx, timeMs, reduceMotion, highContrast, cycle);
    this.sand(ctx, highContrast, cycle);
    this.beachDetails(ctx, cycle);
    this.volleyballNet(ctx);
    this.umbrella(ctx, 248, standingGroundY(248), cycle);
    this.bucket(ctx, 188, standingGroundY(188));
    this.palm(ctx, WORLD_WIDTH - 36, beachGroundY(WORLD_WIDTH - 36), 1);
  }

  private ocean(
    ctx: CanvasRenderingContext2D,
    timeMs: number,
    reduceMotion: boolean,
    highContrast: boolean,
    cycle: number,
  ): void {
    const oceanTop = highContrast ? "#0b5f86" : mixRgb("#1aa6c9", "#c45c4a", cycle * 0.25);
    const oceanBot = highContrast ? "#06344c" : mixRgb("#0b6f8a", "#7a3a48", cycle * 0.2);
    const ocean = ctx.createLinearGradient(0, 430, 0, beachGroundY(WORLD_WIDTH));
    ocean.addColorStop(0, oceanTop);
    ocean.addColorStop(1, oceanBot);
    ctx.fillStyle = ocean;
    ctx.beginPath();
    ctx.moveTo(0, 455);
    const waveT = reduceMotion ? 0 : timeMs / 1000;
    for (let x = 0; x <= WORLD_WIDTH; x += 16) {
      const y = 448 + Math.sin(x * 0.012 + waveT * 1.6) * 8 + Math.sin(x * 0.03 + waveT * 2.2) * 4;
      ctx.lineTo(x, y);
    }
    ctx.lineTo(WORLD_WIDTH, WORLD_HEIGHT);
    ctx.lineTo(0, WORLD_HEIGHT);
    ctx.fill();

    ctx.strokeStyle = "rgba(255,255,255,0.28)";
    ctx.lineWidth = 2;
    for (let i = 0; i < 3; i += 1) {
      ctx.beginPath();
      const y0 = 468 + i * 14;
      for (let x = 0; x <= WORLD_WIDTH; x += 20) {
        const y = y0 + Math.sin(x * 0.02 + waveT * 1.8 + i) * (reduceMotion ? 1.5 : 4);
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }

    ctx.fillStyle = "rgba(255,255,255,0.22)";
    for (let x = 0; x <= WORLD_WIDTH; x += 18) {
      const y = beachGroundY(x) - 10 + Math.sin(x * 0.04 + waveT * 2.4) * (reduceMotion ? 1 : 3);
      ctx.beginPath();
      ctx.ellipse(x, y, 10, 3.2, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    if (!reduceMotion) {
      ctx.fillStyle = "rgba(255,255,255,0.16)";
      for (let i = 0; i < 12; i += 1) {
        const gx = (i * 97 + timeMs * 0.02) % WORLD_WIDTH;
        const gy = 500 + ((i * 37) % 70);
        if (gy < beachGroundY(gx) - 16) {
          ctx.beginPath();
          ctx.ellipse(gx, gy, 7, 2.2, 0.2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
  }

  private sand(ctx: CanvasRenderingContext2D, highContrast: boolean, cycle: number): void {
    ctx.fillStyle = highContrast ? "#c9a24a" : mixRgb("#f3d27a", "#e0a45a", cycle * 0.35);
    ctx.beginPath();
    ctx.moveTo(0, beachGroundY(0));
    for (let x = 16; x <= WORLD_WIDTH; x += 16) ctx.lineTo(x, beachGroundY(x));
    ctx.lineTo(WORLD_WIDTH, WORLD_HEIGHT);
    ctx.lineTo(0, WORLD_HEIGHT);
    ctx.fill();

    ctx.fillStyle = "rgba(196, 154, 72, 0.28)";
    ctx.beginPath();
    ctx.moveTo(0, beachGroundY(0));
    for (let x = 16; x <= WORLD_WIDTH; x += 16) ctx.lineTo(x, beachGroundY(x));
    for (let x = WORLD_WIDTH; x >= 0; x -= 16) ctx.lineTo(x, standingGroundY(x) - 8);
    ctx.fill();
  }

  private beachDetails(ctx: CanvasRenderingContext2D, cycle: number): void {
    this.footprints(ctx);
    this.grass(ctx, 70, standingGroundY(70) + 8, cycle);
    this.grass(ctx, 150, standingGroundY(150) + 18, cycle);
    this.grass(ctx, 1220, standingGroundY(1220) + 10, cycle);
    this.shell(ctx, 430, standingGroundY(430) + 28, 0.9);
    this.shell(ctx, 980, standingGroundY(980) + 36, 1.1);
    this.shell(ctx, 620, standingGroundY(620) + 44, 0.7);
    this.rock(ctx, 510, standingGroundY(510) + 32, 10);
    this.rock(ctx, 860, standingGroundY(860) + 40, 7);
    this.rock(ctx, 160, standingGroundY(160) + 22, 6);
    this.starfish(ctx, 700, standingGroundY(700) + 38, "#ff8a6b");
    this.starfish(ctx, 1120, standingGroundY(1120) + 42, "#ffb347");
    this.towel(ctx, 330, standingGroundY(330) + 10);
  }

  private footprints(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = "rgba(180, 130, 60, 0.22)";
    const pts = [
      [400, 28],
      [418, 34],
      [438, 30],
      [456, 36],
      [476, 32],
      [494, 38],
    ];
    for (const [dx, dy] of pts) {
      const x = dx ?? 400;
      const y = standingGroundY(x) + (dy ?? 30);
      ctx.beginPath();
      ctx.ellipse(x, y, 5, 8, 0.4, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private grass(ctx: CanvasRenderingContext2D, x: number, y: number, cycle: number): void {
    ctx.strokeStyle = mixRgb("#3a9e5a", "#2a6b3a", cycle * 0.2);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    for (let i = 0; i < 7; i += 1) {
      ctx.beginPath();
      ctx.moveTo(x + i * 4 - 10, y);
      ctx.quadraticCurveTo(x + i * 4 - 8, y - 18, x + i * 4 - 4 + (i % 2) * 6, y - 28 - (i % 3) * 4);
      ctx.stroke();
    }
  }

  private shell(ctx: CanvasRenderingContext2D, x: number, y: number, s: number): void {
    ctx.fillStyle = "rgba(40, 30, 16, 0.12)";
    ctx.beginPath();
    ctx.ellipse(x + 2, y + 4, 10 * s, 4 * s, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#f4e2c8";
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.quadraticCurveTo(x + 12 * s, y - 8 * s, x + 16 * s, y + 2 * s);
    ctx.quadraticCurveTo(x + 6 * s, y + 8 * s, x, y);
    ctx.fill();
    ctx.strokeStyle = "rgba(180, 120, 80, 0.5)";
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  private rock(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
    ctx.fillStyle = "rgba(40, 30, 16, 0.14)";
    ctx.beginPath();
    ctx.ellipse(x + 2, y + 4, r * 1.1, r * 0.4, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#b7a089";
    ctx.beginPath();
    ctx.ellipse(x, y, r, r * 0.62, -0.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.18)";
    ctx.beginPath();
    ctx.ellipse(x - r * 0.3, y - r * 0.15, r * 0.3, r * 0.15, -0.4, 0, Math.PI * 2);
    ctx.fill();
  }

  private starfish(ctx: CanvasRenderingContext2D, x: number, y: number, color: string): void {
    ctx.fillStyle = "rgba(40, 30, 16, 0.12)";
    ctx.beginPath();
    ctx.ellipse(x + 2, y + 3, 10, 4, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = color;
    ctx.beginPath();
    for (let i = 0; i < 5; i += 1) {
      const a = -Math.PI / 2 + (i * Math.PI * 2) / 5;
      const x1 = x + Math.cos(a) * 11;
      const y1 = y + Math.sin(a) * 11;
      const x2 = x + Math.cos(a + Math.PI / 5) * 4;
      const y2 = y + Math.sin(a + Math.PI / 5) * 4;
      if (i === 0) ctx.moveTo(x1, y1);
      else ctx.lineTo(x1, y1);
      ctx.lineTo(x2, y2);
    }
    ctx.closePath();
    ctx.fill();
  }

  private towel(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    ctx.fillStyle = "rgba(40, 30, 16, 0.12)";
    ctx.beginPath();
    ctx.ellipse(x + 34, y + 16, 40, 8, 0.05, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#3db8ff";
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 78, y + 4);
    ctx.lineTo(x + 72, y + 30);
    ctx.lineTo(x - 4, y + 24);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#fff";
    ctx.fillRect(x + 10, y + 8, 52, 5);
    ctx.fillStyle = "#ff6b8a";
    ctx.fillRect(x + 8, y + 16, 54, 5);
  }

  private volleyballNet(ctx: CanvasRenderingContext2D): void {
    const x1 = 28;
    const x2 = 96;
    const sand1 = beachGroundY(x1) + 18;
    const sand2 = beachGroundY(x2) + 16;
    const top = Math.min(sand1, sand2) - 168;

    ctx.fillStyle = "rgba(40, 30, 16, 0.16)";
    ctx.beginPath();
    ctx.ellipse(x1 + 2, sand1 + 6, 10, 5, 0, 0, Math.PI * 2);
    ctx.ellipse(x2 + 2, sand2 + 6, 10, 5, 0, 0, Math.PI * 2);
    ctx.fill();

    this.post(ctx, x1, sand1, top);
    this.post(ctx, x2, sand2, top);

    const topY = top + 8;
    const botY = top + 118;
    ctx.fillStyle = "rgba(255,255,255,0.16)";
    ctx.fillRect(x1 + 5, topY, x2 - x1 - 10, botY - topY);

    ctx.strokeStyle = "rgba(255,255,255,0.55)";
    ctx.lineWidth = 1;
    for (let x = x1 + 10; x < x2 - 4; x += 8) {
      ctx.beginPath();
      ctx.moveTo(x, topY + 6);
      ctx.lineTo(x, botY - 6);
      ctx.stroke();
    }
    for (let y = topY + 10; y < botY - 4; y += 8) {
      ctx.beginPath();
      ctx.moveTo(x1 + 8, y);
      ctx.lineTo(x2 - 8, y);
      ctx.stroke();
    }

    ctx.strokeStyle = "#f4f1ea";
    ctx.lineWidth = 5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(x1 + 4, topY);
    ctx.lineTo(x2 - 4, topY);
    ctx.moveTo(x1 + 4, botY);
    ctx.lineTo(x2 - 4, botY);
    ctx.stroke();
    ctx.strokeStyle = "rgba(30, 40, 50, 0.2)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x1 + 6, topY + 3);
    ctx.lineTo(x2 - 2, topY + 3);
    ctx.stroke();
  }

  private post(ctx: CanvasRenderingContext2D, x: number, sandY: number, top: number): void {
    const g = ctx.createLinearGradient(x - 4, top, x + 6, sandY);
    g.addColorStop(0, "#d8c4a0");
    g.addColorStop(0.5, "#b98a4a");
    g.addColorStop(1, "#8a5a28");
    ctx.fillStyle = g;
    fillRoundRect(ctx, x - 4, top, 8, sandY - top + 14, 3);
    ctx.fillStyle = "#6b3f18";
    ctx.fillRect(x - 6, sandY - 4, 12, 10);
  }

  private umbrella(ctx: CanvasRenderingContext2D, x: number, y: number, cycle: number): void {
    ctx.fillStyle = "rgba(40, 30, 16, 0.14)";
    ctx.beginPath();
    ctx.ellipse(x + 4, y + 6, 16, 5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#c9a06a";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x - 8, y - 78);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x - 8, y - 78);
    for (let i = 0; i <= 8; i += 1) {
      const a = Math.PI + (i / 8) * Math.PI;
      ctx.lineTo(x - 8 + Math.cos(a) * 48, y - 78 + Math.sin(a) * 20);
    }
    ctx.closePath();
    ctx.fillStyle = mixRgb("#ff8a5b", "#d4534d", cycle * 0.2);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.45)";
    ctx.lineWidth = 1.2;
    for (let i = 1; i < 8; i += 1) {
      const a = Math.PI + (i / 8) * Math.PI;
      ctx.beginPath();
      ctx.moveTo(x - 8, y - 78);
      ctx.lineTo(x - 8 + Math.cos(a) * 48, y - 78 + Math.sin(a) * 20);
      ctx.stroke();
    }
  }

  private cloud(ctx: CanvasRenderingContext2D, cloud: Cloud, cycle: number): void {
    ctx.fillStyle = `rgba(255, ${Math.round(255 - cycle * 20)}, ${Math.round(255 - cycle * 40)}, 0.88)`;
    const { x, y, s } = cloud;
    ctx.beginPath();
    ctx.arc(x, y, 18 * s, 0, Math.PI * 2);
    ctx.arc(x + 22 * s, y - 10 * s, 24 * s, 0, Math.PI * 2);
    ctx.arc(x + 48 * s, y - 4 * s, 18 * s, 0, Math.PI * 2);
    ctx.arc(x + 28 * s, y + 8 * s, 16 * s, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.beginPath();
    ctx.arc(x + 18 * s, y - 8 * s, 10 * s, 0, Math.PI * 2);
    ctx.fill();
  }

  private bird(ctx: CanvasRenderingContext2D, bird: Bird): void {
    const flap = Math.sin(bird.phase) * 7;
    ctx.strokeStyle = "#243048";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(bird.x - 14, bird.y + flap * 0.35);
    ctx.quadraticCurveTo(bird.x - 4, bird.y - 7 - flap, bird.x, bird.y + 1);
    ctx.quadraticCurveTo(bird.x + 4, bird.y - 7 - flap, bird.x + 14, bird.y + flap * 0.35);
    ctx.stroke();
    ctx.fillStyle = "#243048";
    ctx.beginPath();
    ctx.ellipse(bird.x, bird.y + 1, 3.2, 2.2, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  private palm(ctx: CanvasRenderingContext2D, x: number, y: number, dir: number): void {
    ctx.fillStyle = "rgba(40, 30, 16, 0.16)";
    ctx.beginPath();
    ctx.ellipse(x, y + 18, 18, 7, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#a56b3c";
    ctx.lineWidth = 12;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(x, y + 36);
    ctx.quadraticCurveTo(x + dir * 18, y - 40, x + dir * 8, y - 118);
    ctx.stroke();
    ctx.strokeStyle = "rgba(80, 40, 16, 0.25)";
    ctx.lineWidth = 2;
    for (let i = 0; i < 5; i += 1) {
      ctx.beginPath();
      ctx.moveTo(x - 4, y + 20 - i * 22);
      ctx.lineTo(x + 4, y + 12 - i * 22);
      ctx.stroke();
    }
    ctx.fillStyle = "#2f9e5a";
    for (let i = 0; i < 6; i += 1) {
      ctx.beginPath();
      ctx.moveTo(x + dir * 8, y - 118);
      ctx.quadraticCurveTo(x + dir * (46 - i * 8), y - 96 - i * 8, x + dir * (78 - i * 10), y - 74 - i * 6);
      ctx.quadraticCurveTo(x + dir * 22, y - 108, x + dir * 8, y - 118);
      ctx.fill();
    }
  }

  private bucket(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    ctx.fillStyle = "rgba(40, 30, 16, 0.14)";
    ctx.beginPath();
    ctx.ellipse(x + 8, y + 4, 22, 6, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#ff8a5b";
    ctx.beginPath();
    ctx.moveTo(x - 16, y - 26);
    ctx.lineTo(x + 16, y - 26);
    ctx.lineTo(x + 12, y);
    ctx.lineTo(x - 12, y);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "#d2653d";
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.strokeStyle = "#ffe0c8";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(x, y - 26, 12, Math.PI, 0);
    ctx.stroke();
    ctx.fillStyle = "#ffd24a";
    ctx.save();
    ctx.translate(x + 22, y - 8);
    ctx.rotate(-0.5);
    ctx.fillRect(-4, -22, 8, 24);
    ctx.fillRect(-10, -28, 20, 8);
    ctx.restore();
  }
}

function mix(a: number[], b: number[], t: number): number[] {
  return a.map((v, i) => v + ((b[i] ?? v) - v) * t);
}

function rgb(c: number[]): string {
  return `rgb(${c[0] ?? 0}, ${c[1] ?? 0}, ${c[2] ?? 0})`;
}

function mixRgb(a: string, b: string, t: number): string {
  const pa = hex(a);
  const pb = hex(b);
  return rgb(mix(pa, pb, t));
}

function hex(value: string): number[] {
  const n = value.replace("#", "");
  return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
}

function fillRoundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
  ctx.fill();
}
